import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ArrowIosDownward, CheckMark } from '@lyearn/icons';
import { Loader, Stack, Typography } from '@lyearn/ui';
import { TypographyVariants } from '@lyearn/ui';
import useUser from '@/components/Form/components/Conditions/hooks/useUser';
import UserSearch from '@/components/UsersSearch';
import UserDetail from '@/components/UsersSearch/components/UserDetail';
import { UserType, UserTypeEnum } from '@/types/schema';

import { autocompleteClasses, autocompleteStyles } from './styles';

const USER_TYPES = [UserTypeEnum.Staff];
const USER_DETAILS_OVERRIDES = {
  Name: {
    Typography: {
      variant: 'body-short-01' as TypographyVariants,
    },
  },
};

function Assigner({
  assignerId,
  onChange,
  disabled,
}: {
  assignerId?: string;
  disabled?: boolean;
  onChange: (val: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const { fetching, user: assignerUser } = useUser(assignerId);
  const resolvedUser = assignerUser as UserType | undefined;

  return (
    <Stack className="w-1/2" direction="col" gapY={8}>
      {fetching ? (
        <Stack
          alignItems="center"
          className="h-48 w-full rounded-8 border border-interactive-border pe-12 ps-16">
          <Loader arcColorToken="icon-active" circleColorToken="icon-placeholder" size={24} />
          <Typography className="ms-12" color="text-text-placeholder" variant="body-short-01">
            {t('Common:Loading')}
          </Typography>
        </Stack>
      ) : (
        <UserSearch
          className={autocompleteStyles}
          classes={autocompleteClasses}
          disabled={disabled}
          initalValue={resolvedUser?.name}
          placeholder={t('AssignmentCreator:AssignedBy')}
          renderEndAdornment={() => (
            <ArrowIosDownward className="h-22 w-22" fillColorToken="icon-secondary" />
          )}
          renderOption={(user: UserType) => {
            return (
              <UserDetail
                key={user.id}
                endAdornment={
                  user.id === assignerId ? (
                    <CheckMark className="h-20 w-20" fillColorToken="icon-active" />
                  ) : null
                }
                overrides={USER_DETAILS_OVERRIDES}
                showEmail={false}
                user={user}
              />
            );
          }}
          userTypes={USER_TYPES}
          value={resolvedUser}
          onChange={onChange}
        />
      )}
    </Stack>
  );
}

export default React.memo(Assigner);
